import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "sonner";
import SessionTimeoutWarning from "./components/SessionTimeoutWarning";

// Pages
import LandingPage from "./pages/LandingPage";
import AuthPage from "./pages/AuthPage";

// Layouts
import DashboardLayout from "./components/layout/DashboardLayout";

// Patient Pages
import PatientDashboard from "./pages/patient/PatientDashboard";
import PatientIntake from "./pages/patient/PatientIntake";
import AppointmentsPage from "./pages/patient/AppointmentsPage";
import WaitingRoomPage from "./pages/patient/WaitingRoomPage";
import InsuranceBillingPage from "./pages/patient/InsuranceBillingPage";
import HIPAAConsentPage from "./pages/patient/HIPAAConsentPage";
import DocumentsPage from "./pages/patient/DocumentsPage";
import MedicationsPage from "./pages/patient/MedicationsPage";
import HealthRecordsPage from "./pages/patient/HealthRecordsPage";
import PatientAICMAPage from "./pages/patient/PatientAICMAPage";
import PatientHomeLabsPage from "./pages/patient/PatientHomeLabsPage";
import PatientHealthDevicesPage from "./pages/patient/PatientHealthDevicesPage";
import DexcomCallbackPage from "./pages/patient/DexcomCallbackPage";
import AICheckInReviewsPage from "./pages/staff/AICheckInReviewsPage";

// CMA Pages
import CMADashboard from "./pages/cma/CMADashboard";
import TriagePage from "./pages/cma/TriagePage";
import MedicationReconciliationPage from "./pages/cma/MedicationReconciliationPage";
import PatientQueuePage from "./pages/cma/PatientQueuePage";
import CMACheckoutQueuePage from "./pages/cma/CMACheckoutQueuePage";
import CMACheckoutPage from "./pages/cma/CMACheckoutPage";
import CMAChartEditPage from "./pages/cma/CMAChartEditPage";

// Staff Shared Pages
import StaffHomeLabsPage from "./pages/staff/StaffHomeLabsPage";

// Shared Pages
import LabResultsPage from "./pages/shared/LabResultsPage";

// Provider Pages
import ProviderDashboard from "./pages/provider/ProviderDashboard";
import EncounterPage from "./pages/provider/EncounterPage";
import AIScribePage from "./pages/provider/AIScribePage";
import EncounterSearchPage from "./pages/provider/EncounterSearchPage";
import ProviderHealthDevicesPage from "./pages/provider/ProviderHealthDevicesPage";

// Admin Pages
import AdminDashboard from "./pages/admin/AdminDashboard";
import AnalyticsPage from "./pages/admin/AnalyticsPage";
import UserManagementPage from "./pages/admin/UserManagementPage";
import MedicalBillingPage from "./pages/admin/MedicalBillingPage";
import ClaimsManagementPage from "./pages/admin/ClaimsManagementPage";
import PatientStatementPage from "./pages/admin/PatientStatementPage";
import PaymentPlansPage from "./pages/admin/PaymentPlansPage";
import ARAgingReportPage from "./pages/admin/ARAgingReportPage";
import EOBImportPage from "./pages/admin/EOBImportPage";
import SuperbillPage from "./pages/admin/SuperbillPage";
import DenialManagementPage from "./pages/admin/DenialManagementPage";
import PilotProgramPage from "./pages/admin/PilotProgramPage";
import EndOfDayReportPage from "./pages/admin/EndOfDayReportPage";
import AdminConsentsPage from "./pages/admin/AdminConsentsPage";
import AdminLabsDocumentsPage from "./pages/admin/AdminLabsDocumentsPage";
import AdminTelehealthPage from "./pages/admin/AdminTelehealthPage";
import AdminRemindersPage from "./pages/admin/AdminRemindersPage";
import AdminReportsPage from "./pages/admin/AdminReportsPage";
import AdminScheduledReportsPage from "./pages/admin/AdminScheduledReportsPage";

// Patient Pages
import PatientRefillsPage from "./pages/patient/PatientRefillsPage";

// Staff Pages
import RefillManagementPage from "./pages/staff/RefillManagementPage";

// Shared Pages
import MessagesPage from "./pages/shared/MessagesPage";
import PrescriptionsPage from "./pages/shared/PrescriptionsPage";
import CareGapsPage from "./pages/shared/CareGapsPage";
import NotificationsPage from "./pages/shared/NotificationsPage";
import AIAssistantPage from "./pages/shared/AIAssistantPage";
import VideoCallsPage from "./pages/shared/VideoCallsPage";
import TelehealthRoomPage from "./pages/shared/TelehealthRoomPage";
import PatientChartPage from "./pages/shared/PatientChartPage";
import PatientListPage from "./pages/shared/PatientListPage";
import PatientDashboardPage from "./pages/shared/PatientDashboardPage";
import MedicareFormsPage from "./pages/shared/MedicareFormsPage";
import TrainingPage from "./pages/shared/TrainingPage";
import VideoCallPage from "./pages/shared/VideoCallPage";

// Staff Pages
import DailySchedulePage from "./pages/staff/DailySchedulePage";

// Protected Route Component
function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    switch (user.role) {
      case 'patient':
        return <Navigate to="/patient/appointments" replace />;
      case 'cma':
        return <Navigate to="/cma" replace />;
      case 'provider':
        return <Navigate to="/provider" replace />;
      case 'admin':
        return <Navigate to="/admin" replace />;
      default:
        return <Navigate to="/" replace />;
    }
  }

  return children;
}

// Placeholder pages for routes not yet implemented
function PlaceholderPage({ title }) {
  return (
    <div className="p-8">
      <h1 className="text-2xl font-bold text-slate-900 tracking-tight mb-4" style={{ fontFamily: 'Manrope' }}>
        {title}
      </h1>
      <p className="text-slate-500">This page is under construction.</p>
    </div>
  );
}

function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={user ? <Navigate to={`/${user.role === 'patient' ? 'patient/appointments' : user.role === 'cma' ? 'staff/schedule' : user.role}`} /> : <LandingPage />} />
      <Route path="/login" element={user ? <Navigate to={`/${user.role === 'patient' ? 'patient/appointments' : user.role === 'cma' ? 'staff/schedule' : user.role}`} /> : <AuthPage />} />

      {/* Patient Routes */}
      <Route path="/patient" element={
        <ProtectedRoute allowedRoles={['patient']}>
          <DashboardLayout />
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="/patient/appointments" replace />} />
        <Route path="intake" element={<PatientIntake />} />
        <Route path="appointments" element={<AppointmentsPage />} />
        <Route path="messages" element={<MessagesPage />} />
        <Route path="insurance" element={<InsuranceBillingPage />} />
        <Route path="insurance-id" element={<InsuranceBillingPage />} />
        <Route path="statement" element={<PatientStatementPage />} />
        <Route path="documents" element={<DocumentsPage />} />
        <Route path="prescriptions" element={<PrescriptionsPage />} />
        <Route path="medications" element={<MedicationsPage />} />
        <Route path="refills" element={<PatientRefillsPage />} />
        <Route path="health-records" element={<HealthRecordsPage />} />
        <Route path="medicare-forms" element={<MedicareFormsPage />} />
        <Route path="hipaa" element={<HIPAAConsentPage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="ai-assistant" element={<AIAssistantPage />} />
        <Route path="ai-cma" element={<PatientAICMAPage />} />
        <Route path="home-labs" element={<PatientHomeLabsPage />} />
        <Route path="health-devices" element={<PatientHealthDevicesPage />} />
        <Route path="health-devices/dexcom-callback" element={<DexcomCallbackPage />} />
        <Route path="video-call/:appointmentId" element={<VideoCallPage />} />
        <Route path="waiting-room/:appointmentId" element={<WaitingRoomPage />} />
      </Route>

      {/* CMA Routes */}
      <Route path="/cma" element={
        <ProtectedRoute allowedRoles={['cma', 'admin']}>
          <DashboardLayout />
        </ProtectedRoute>
      }>
        <Route index element={<CMADashboard />} />
        <Route path="queue" element={<PatientQueuePage />} />
        <Route path="triage" element={<PatientQueuePage />} />
        <Route path="triage/:intakeId" element={<TriagePage />} />
        <Route path="checkout-queue" element={<CMACheckoutQueuePage />} />
        <Route path="checkout/:checkoutId" element={<CMACheckoutPage />} />
        <Route path="medications" element={<MedicationReconciliationPage />} />
        <Route path="labs" element={<LabResultsPage />} />
        <Route path="chart/:patientId/:appointmentId" element={<CMAChartEditPage />} />
        <Route path="ai-assist" element={<AIAssistantPage />} />
        <Route path="insurance" element={<PlaceholderPage title="Insurance Verification" />} />
        <Route path="care-gaps" element={<CareGapsPage />} />
        <Route path="messages" element={<MessagesPage />} />
        <Route path="video-calls" element={<TelehealthRoomPage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="end-of-day-report" element={<EndOfDayReportPage />} />
        <Route path="health-devices" element={<ProviderHealthDevicesPage />} />
      </Route>

      {/* Staff Routes (Shared between CMA, Provider, Admin, Front Desk) */}
      <Route path="/staff" element={
        <ProtectedRoute allowedRoles={['cma', 'provider', 'admin', 'front_desk']}>
          <DashboardLayout />
        </ProtectedRoute>
      }>
        <Route path="schedule" element={<DailySchedulePage />} />
        <Route path="patients" element={<PatientListPage />} />
        <Route path="patients/:patientId/chart" element={<PatientChartPage />} />
        <Route path="patients/:patientId/dashboard" element={<PatientDashboardPage />} />
        <Route path="refills" element={<RefillManagementPage />} />
        <Route path="home-labs" element={<StaffHomeLabsPage />} />
        <Route path="ai-checkins" element={<AICheckInReviewsPage />} />
        <Route path="medicare-forms" element={<MedicareFormsPage />} />
        <Route path="training" element={<TrainingPage />} />
      </Route>

      {/* Provider Routes */}
      <Route path="/provider" element={
        <ProtectedRoute allowedRoles={['provider', 'admin']}>
          <DashboardLayout />
        </ProtectedRoute>
      }>
        <Route index element={<ProviderDashboard />} />
        <Route path="encounters" element={<ProviderDashboard />} />
        <Route path="encounters/:encounterId" element={<EncounterPage />} />
        <Route path="encounter-search" element={<EncounterSearchPage />} />
        <Route path="ai-scribe" element={<AIScribePage />} />
        <Route path="medications" element={<MedicationReconciliationPage />} />
        <Route path="labs" element={<LabResultsPage />} />
        <Route path="messages" element={<MessagesPage />} />
        <Route path="video-calls" element={<TelehealthRoomPage />} />
        <Route path="prescriptions" element={<PrescriptionsPage />} />
        <Route path="care-gaps" element={<CareGapsPage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="health-devices" element={<ProviderHealthDevicesPage />} />
      </Route>

      {/* Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <DashboardLayout />
        </ProtectedRoute>
      }>
        <Route index element={<AdminDashboard />} />
        <Route path="users" element={<UserManagementPage />} />
        <Route path="consents" element={<AdminConsentsPage />} />
        <Route path="labs-documents" element={<AdminLabsDocumentsPage />} />
        <Route path="telehealth" element={<AdminTelehealthPage />} />
        <Route path="reminders" element={<AdminRemindersPage />} />
        <Route path="reports" element={<AdminReportsPage />} />
        <Route path="scheduled-reports" element={<AdminScheduledReportsPage />} />
        <Route path="analytics" element={<AnalyticsPage />} />
        <Route path="settings" element={<PlaceholderPage title="Settings" />} />
        <Route path="billing" element={<InsuranceBillingPage />} />
        <Route path="medical-billing" element={<MedicalBillingPage />} />
        <Route path="claims" element={<ClaimsManagementPage />} />
        <Route path="statements" element={<PatientStatementPage />} />
        <Route path="payment-plans" element={<PaymentPlansPage />} />
        <Route path="ar-aging" element={<ARAgingReportPage />} />
        <Route path="eob-import" element={<EOBImportPage />} />
        <Route path="superbills" element={<SuperbillPage />} />
        <Route path="denials" element={<DenialManagementPage />} />
        <Route path="pilot-program" element={<PilotProgramPage />} />
        <Route path="care-gaps" element={<CareGapsPage />} />
        <Route path="messages" element={<MessagesPage />} />
        <Route path="video-calls" element={<VideoCallsPage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="ai-assistant" element={<AIAssistantPage />} />
        <Route path="end-of-day-report" element={<EndOfDayReportPage />} />
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="App">
          <AppRoutes />
          <SessionTimeoutWarning />
          <Toaster position="top-right" richColors />
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
